<template>
  <div class="orderProgress">
    <card-m
      :title="title"
      cardWidth="100%"
      cardHeight="100%"
      maxHeight="unset"
      toolWidth="70%"
      toolJustifyontent="flex-start"
      :show-filter="false"
      :show-export="true"
      :show-column-edit="true"
      export-file-name="订单列表"
      @export="handleExport"
      @edit-column="openColumnEdit"
    >
      <template slot="card-box">
        <!-- 一级：订单列表
             列全部走 card-table 的列定义（进度条 / 操作按钮 / 编辑列都在 card-table 里实现） -->
        <div class="op-table-wrap">
          <card-table
            ref="orderTable"
            tableID="orderProgressTable"
            row-key="__key"
            :expand-multiple="true"
            :tableColumn="effectiveVisibleColumns"
            :mobile-columns="mobileColumnEditFilter"
            :key="tableRenderKey"
            :tableData="rows"
            :total="total"
            :currentPage="pageNo"
            :size="pageSize"
            :loading="loading"
            :showPaging="true"
            :showIndex="true"
            style="height: 100%;"
            @sizeChange="onOrderSizeChange"
            @currentChange="onOrderPageChange"
            @expandChange="onExpandChange"
            @handleDesignClick="onOrderDesignClick"
            @linkClick="onModelLinkClick"
            @column-width-change="onColumnWidthChange"
          >
            <!-- 展开列（藏起来的那一列）：必须保留 type="expand"，Element 靠它渲染展开行的内容，
                 箭头图标在样式里隐藏，改由「产品型号」的蓝色链接点击展开 / 收起 -->
            <template slot="expandColumn">
              <el-table-column type="expand" width="1">
                <template slot-scope="scope">
                  <div class="sub-wrap">
                    <!-- ✅ 展开区页签：任务单列表 / BOM列表（每行各自独立，默认任务单列表）
                         任务单：展开时加载（onExpandChange）
                         BOM：切到该页签才懒加载（getBomsListFromContract，参数 fsn=短代码），
                              列按接口返回字段动态生成，后端传什么就显示什么 -->
                    <el-tabs v-model="scope.row.__subTab" class="sub-tabs" @tab-click="onSubTabClick(scope.row, $event)">
                      <el-tab-pane label="任务单列表" name="task" lazy>
                        <div class="sub-table">
                          <card-table
                            :tableID="'taskTable_' + scope.row.__key"
                            row-key="__taskKey"
                            :tableColumn="taskColumns"
                            :mobile-columns="allTaskProps"
                            :tableData="scope.row.__tasks"
                            :loading="scope.row.__taskLoading"
                            :showPaging="false"
                            :showIndex="true"
                            :columnFilterValues="scope.row.__filters"
                            @textEditSave="handleTextEditSave"
                            @dateEditSave="handleDateEditSave"
                            @icon-click="handleStatusIconClick"
                            @handleProductionClick="onTaskProductionClick"
                            @column-filter-change="onTaskFilterChange(scope.row, $event)"
                          />
                        </div>

                        <!-- 任务单分页（BOM 有自己的分页，所以各页签内放各自的） -->
                        <div class="sub-paging">
                          <paging
                            :responsive="false"
                            :current="scope.row.__taskPage"
                            :size="scope.row.__taskSize"
                            :total="scope.row.__taskTotal"
                            :pageSizes="[10, 20, 50, 100]"
                            @sizeChange="onTaskSizeChange(scope.row, $event)"
                            @currentChange="onTaskPageChange(scope.row, $event)"
                          />
                        </div>
                      </el-tab-pane>

                      <el-tab-pane label="BOM列表" name="bom" lazy>
                        <div class="sub-table">
                          <card-table
                            :tableID="'bomTable_' + scope.row.__key"
                            row-key="__bomKey"
                            :tableColumn="allBomColumns"
                            :mobile-columns="allBomProps"
                            :tableData="scope.row.__bomData"
                            :loading="scope.row.__bomLoading"
                            :showPaging="false"
                            :showIndex="true"
                          />
                        </div>

                        <!-- BOM 分页 -->
                        <div class="sub-paging">
                          <paging
                            :responsive="false"
                            :current="scope.row.__bomPage"
                            :size="scope.row.__bomSize"
                            :total="scope.row.__bomTotal"
                            :pageSizes="[10, 20, 50, 100]"
                            @sizeChange="onBomSizeChange(scope.row, $event)"
                            @currentChange="onBomPageChange(scope.row, $event)"
                          />
                        </div>
                      </el-tab-pane>
                    </el-tabs>
                  </div>
                </template>
              </el-table-column>
            </template>
          </card-table>
        </div>
      </template>
    </card-m>

    <!-- ✅ 订单列表列编辑弹窗（显隐 / 拖拽排序 / 列宽）
         电脑端偏好存 orderProgress，手机端存 orderProgress_m（由 mixin 的 pageType 派生） -->
    <column-edit-dialog
      :visible.sync="columnEditVisible"
      :columns="tempOrderedColumns"
      :defaultColumns="effectiveDefaultColumnDefs"
      :title="columnEditTitle"
      :dialogWidth="columnEditDialogWidth"
      @confirm="handleColumnSave"
      @cancel="handleColumnCancel"
    />

    <!-- 任务单异常详情弹窗（点状态图标 → 异常行才弹） -->
    <common-dialog-table
      :visible="exceptionDialogVisible"
      :title="'任务单异常详情 - 任务号: ' + currentExceptionTaskNo"
      :tableColumn="exceptionListColumn"
      :tableData="exceptionList"
      @close="closeExceptionDialog"
    />
  </div>
</template>

<script>
import FileSaver from 'file-saver';
import XLSX from 'xlsx';
import UserInfoUtils from '@/utils/userInfoUtils.js';
import CommonDialogTable from '@/components/card/CommonDialogTable.vue';
import columnEditMixin from '@/mixins/columnEditMixin';
import ColumnEditDialog from '@/components/ColumnEditDialog.vue';
import {
  getOrdersListFromProject,
  getOrdersListFromContract,
  getTasksListFromProject,
  getTasksListFromContract,
  getBomsListFromContract,
  UpdateDataList,
  getAbnormalInfo
} from '@/api/mainapi.js';

export default {
  name: 'orderProgress',
  mixins: [columnEditMixin],

  components: { CommonDialogTable, ColumnEditDialog },

  props: {
    /** 合同号：一级 / 二级接口都要传 */
    contractNo: {
      type: String,
      default: ''
    },
    /** 入口来源：项目维度 → dash/project/*；合同维度 → dash/contract/* */
    isFromProject: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '订单列表'
    },
    /** 一级筛选项（可不要）：型号 */
    filterModel: {
      type: String,
      default: ''
    },
    /** 一级筛选项（可不要）：数量 */
    filterQty: {
      type: String,
      default: '0'
    }
  },

  data() {
    return {
      /* ✅ 列偏好页面标识（columnEditMixin 需要）
         电脑端存 orderProgress，手机端自动派生 orderProgress_m，两端各自独立保存 */
      pageType: 'orderProgress',

      /* 手机端可用列（订单列表全部列）—— 恢复默认时这些列打钩 */
      mobileColumnFilter: [
        'FModel', '合同号', '短代码', '物料属性', '数量',
        'BOM进度', '可用库存', '在途数', '备注', '操作'
      ],
      /* 手机端列编辑弹窗中可勾选的全部列（默认与上面一致 = 全部列） */
      mobileColumnEditFilter: [
        'FModel', '合同号', '短代码', '物料属性', '数量',
        'BOM进度', '可用库存', '在途数', '备注', '操作'
      ],

      /* ---------------- 一级：订单列表 ---------------- */
      rows: [],
      total: 0,
      pageNo: 1,
      pageSize: 50,   /* 和 productProgress 一致；也是 card-table 默认 pageSizes 的第一项 */
      loading: false,

      orderColumns: [
        /* 规格型号：蓝色链接列（card-table 自带的 link 类型），点击 → 展开 / 收起该行
           ⚠️ 物料属性为「外购」时没有下级（BOM/任务）可展开，
              该行的型号渲染成普通文本、不可点击（由 card-table 的 disabledField 支持） */
        {
          prop: 'FModel',
          label: '规格型号',
          custom: true,
          column: {
            type: 'link',
            field: 'FModel',
            // 风格对齐「操作」列里的「设计」按钮（Element el-button--text 同款蓝）
            linkStyle: { color: '#409eff' },
            // ✅ 只有「物料属性 = 自制」才能点开子列表；外购 / 空 / 其它值都是普通文本
            enabledField: '物料属性',
            enabledValues: ['自制']
          }
        },
        { prop: '合同号', label: '合同号' },
        { prop: '短代码', label: '短代码' },        
        { prop: '物料属性', label: '物料属性' },
        { prop: '数量', label: '数量', width: 90 },
        {
          prop: 'BOM进度',
          label: 'BOM进度',
          width: 100,
          custom: true,
          column: { type: 'progress', field: 'BOM进度' }
        },
        { prop: '可用库存', label: '可用库存', width: 90 },
        { prop: '在途数', label: '在途数', width: 90 },

        
        { prop: '备注', label: '备注' },
        {
          prop: '操作',
          label: '操作',
          width: 80,
          actions: [
            { label: '设计', fnName: 'handleDesignClick' }
          ]
        }
      ],

      /* ---------------- 二级：任务单列表 ----------------
         finterid / fstockflag 是内部字段，不展示 */
      taskColumns: [
        {
          prop: '数据状态码',   // 0 异常 / 1 完全入库 / 2 下达·结案 / 4 其它
          label: '状态',
          width: 55,
          custom: true,
          column: {
            type: 'icon',
            icon: { default: 'status-circle' },
            tooltipField: '状态详情'
          }
        },
        { prop: '任务号', label: '任务号'},
        { prop: '子项', label: '规格型号' },

        { prop: '生产车间', label: '生产车间' },
        { prop: '生产进度', label: '生产进度', minWidth: 60},
        {
          prop: '计划完工日期',
          label: '计划完工日期',
          width: 120,
          filterable: true,
          filterType: 'dateFrom'
        },
        {
          prop: '任务单执行反馈',
          label: '任务单执行反馈',
          custom: true,
          column: {
            type: 'textEdit',
            field: '任务单执行反馈',
            maxDisplayLength: 10
          }
        },
        {
          prop: '派工日期',
          label: '派工日期',
          custom: true,
          column: {
            type: 'dateEdit',
            field: '派工日期',
            dateFormat: (date) => {
              if (!date) return '';
              if (typeof date === 'string' && date.length > 10) {
                return date.substring(0, 10);
              }
              return date;
            }
          }
        },
        {
          prop: '领料/投料',
          label: '领料比',
          custom: true,
          column: { type: 'progress', field: '领料/投料' }
        },
        { prop: '领料人', label: '领料人' },
        { prop: '备注', label: '备注' },
        // { prop: 'FShortNumber', label: '短代码' },
        // { prop: '主管', label: '主管' },
        // { prop: '部门', label: '部门' },
        // { prop: '销售员', label: '销售员' },
        // { prop: '物料属性', label: '物料属性' },
        // { prop: '数量', label: '数量', width: 90 },
        // { prop: '可用库存', label: '可用库存', width: 90 },
        // { prop: '在途数', label: '在途数', width: 90 },
        {
          prop: '操作',
          label: '操作',
          width: 80,
          actions: [
            { label: '生产', fnName: 'handleProductionClick' }
          ]
        }
      ],

      /* ---------------- 二级：BOM 列表（页签） ----------------
         ✅ 接口：getBomsListFromContract，参数 fsn/pageNo/pageSize。
         列顺序在这里控制 —— 想调整 BOM 列表的列顺序，直接改下面的数组顺序即可；
         后端新增了这里没写的字段时，会自动追加到最后一列（不会丢数据）。 */
      bomColumns: [
        { prop: '物料名称', label: '物料名称' },
        { prop: '规格型号', label: '规格型号' },
        { prop: '短代码', label: '短代码' },
        { prop: '物料代码', label: '物料代码' },
        { prop: '基本用量', label: '基本用量' },
        { prop: '用量', label: '用量' },
        { prop: '库存', label: '库存' },
        { prop: '单位', label: '单位' },
        { prop: '在途数', label: '在途数' },
        { prop: '待入数', label: '待入数' }
      ],
      // 后端返回但 bomColumns 里没定义的字段（自动补列，全局一份，字段结构各产品一致）
      bomExtraColumns: [],

      /* ---------------- 异常详情弹窗 ---------------- */
      exceptionDialogVisible: false,
      exceptionList: [],
      currentExceptionTaskNo: '',
      exceptionListColumn: [
        { prop: '项目编号', label: '项目编号' },
        { prop: '合同编号', label: '合同编号' },
        { prop: '任务号', label: '任务号' },
        { prop: '相关节点人', label: '相关节点人' },
        { prop: '异常类型', label: '异常类型' },
        { prop: '事件详情', label: '事件详情' },
        { prop: '发生时间', label: '发生时间' },
        { prop: '是否需要处理', label: '是否需要处理' },
        { prop: '处理人', label: '处理人' },
        { prop: '处理时间', label: '处理时间' },
        { prop: '解决时间', label: '解决时间' }
      ]
    };
  },

  computed: {
    /* ✅ 供 columnEditMixin 使用的列定义（订单列表） */
    baseColumnDefs() {
      return this.orderColumns;
    },

    /* ⚠️ card-table 在手机端（≤768px）默认只显示「声明了 showOnMobile 的列」，
       不传 mobileColumns 的话手机上会几乎没列。这里把全部列都交给 mobileColumns，
       手机端就按同样的列渲染 —— 手机 / 电脑显示完全一致。 */
    allOrderProps() {
      return this.orderColumns.map((col) => col.prop);
    },
    allTaskProps() {
      return this.taskColumns.map((col) => col.prop);
    },
    /* BOM 列表最终列：先按 bomColumns 的顺序，再把后端多出来的字段补在后面 */
    allBomColumns() {
      const extra = this.bomExtraColumns.map((key) => ({ prop: key, label: key }));
      return this.bomColumns.concat(extra);
    },
    allBomProps() {
      return this.allBomColumns.map((col) => col.prop);
    }
  },

  watch: {
    // 合同号是异步拿到的（先查合同信息 → 才知道合同编号），拿到后再拉订单列表
    contractNo() {
      this.pageNo = 1;
      this.fetchOrders();
    }
  },

  async mounted() {
    // ✅ 先加载列偏好（电脑端 orderProgress / 手机端 orderProgress_m），再用它渲染表格
    await this.loadColumnPref();
    if (this.contractNo) this.fetchOrders();
  },

  methods: {
    /* ================= 一、通用取值 / 格式化 ================= */
    /** 只保留整数（数量 / 可用库存 / 在途数）：空值、非数字一律置空 */
    toInt(val) {
      if (val === null || val === undefined || val === '') return '';
      const n = Number(val);
      return isNaN(n) ? '' : String(Math.round(n));
    },

    /** 文本取值：对象取 context，字符串「空」当没值（后端用「空」表示空） */
    pickText(val) {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return val.context || '';
      return val === '空' ? '' : String(val);
    },

    /** 日期取值：对象取 dispatchdate / dispatchDate，字符串只取前 10 位 */
    pickDate(val) {
      if (!val || val === '空') return '';
      let s = '';
      if (typeof val === 'object') {
        s = val.dispatchdate || val.dispatchDate || val.date || '';
      } else {
        s = String(val);
      }
      if (!s || s === '空') return '';
      return s.length > 10 ? s.substring(0, 10) : s;
    },

    /* ================= 二、一级：订单列表 ================= */
    normalizeOrder(item, i) {
      return {
        ...item,
        数量: this.toInt(item['数量']),
        可用库存: this.toInt(item['可用库存']),
        在途数: this.toInt(item['在途数']),
        __key: `${this.pageNo}-${i}-${item.FModel || ''}-${item.短代码 || ''}`,
        // 二级：任务单
        __tasks: [],
        __taskTotal: 0,
        __taskPage: 1,
        __taskSize: 10,
        __taskLoading: false,
        __taskLoaded: false,   // 是否已拉过（展开过就不重复请求，除非翻页 / 筛选）
        __filters: {},         // 二级表格的列筛选值
        __subTab: 'task',      // ✅ 展开区当前页签：'task' 任务单列表 / 'bom' BOM列表
        // 二级：BOM（字段在 normalizeOrder 里先占位，后面赋值才是响应式的）
        __bomData: [],
        __bomTotal: 0,
        __bomPage: 1,
        __bomSize: 20,
        __bomLoading: false,
        __bomLoaded: false     // 懒加载：切到 BOM 页签才请求，拉过就不重复
      };
    },

    async fetchOrders() {
      if (!this.contractNo) {
        this.rows = [];
        this.total = 0;
        return;
      }
      this.loading = true;
      const api = this.isFromProject ? getOrdersListFromProject : getOrdersListFromContract;
      try {
        const res = await api({
          contractNo: this.contractNo,
          model: this.filterModel || '',   // 筛选，可不要
          qty: this.filterQty || '0',      // 筛选，可不要
          pageNo: String(this.pageNo),
          pageSize: String(this.pageSize)
        });

        /* ⚠️ 后端没数据时可能返回 data: "信息为空"（字符串），这里统一兜住 */
        const payload = res && res.data && res.data.data;
        const info = payload && typeof payload === 'object' ? payload.information : null;
        if (res && res.data && res.data.code === 200 && info) {
          this.rows = (info.list || []).map((item, i) => this.normalizeOrder(item, i));
          this.total = Number(info.total) || 0;
          this.expandedKeys = [];
        } else {
          this.rows = [];
          this.total = 0;
        }
      } catch (e) {
        this.rows = [];
        this.total = 0;
        this.$message.error('订单列表加载失败，请检查网络');
      } finally {
        this.loading = false;
      }
    },

    onOrderPageChange(p) {
      this.pageNo = p;
      this.fetchOrders();
    },
    onOrderSizeChange(s) {
      this.pageSize = s;
      this.pageNo = 1;
      this.fetchOrders();
    },

    /** 订单层级「设计」按钮：跳转逻辑与任务单层级完全一致，交给父组件处理 */
    onOrderDesignClick(row) {
      this.$emit('design-click', row);
    },

    /* ================= 三、二级：任务单列表 ================= */
    /** 状态码 / 状态详情：逻辑与 workOrderProgress 完全一致 */
    enrichTask(item) {
      const isIssue = item['是否有异常'];
      const progress = item['生产进度'] || '';
      let 数据状态码 = '';
      let 状态详情 = progress;

      if (isIssue === 'true') {
        数据状态码 = 0;          // 有异常 → 红
        状态详情 = '异常，可点击查看详情';
      } else if (progress === '下达' || progress === '结案') {
        数据状态码 = 2;
      } else if (progress === '完全入库') {
        数据状态码 = 1;
      } else if (progress === '异常') {
        数据状态码 = 0;
      } else {
        数据状态码 = 4;
      }
      return { 数据状态码, 状态详情 };
    },

    normalizeTask(item, i) {
      const { 数据状态码, 状态详情 } = this.enrichTask(item);
      return {
        ...item,
        任务单执行反馈: this.pickText(item['执行订单反馈']),  // 后端字段名不同，这里对齐列定义
        派工日期: this.pickDate(item['派工日期']),
        计划完工日期: this.pickDate(item['计划完工日期']),
        数量: this.toInt(item['数量']),
        可用库存: this.toInt(item['可用库存']),
        在途数: this.toInt(item['在途数']),
        备注: item['备注'] || '',
        数据状态码,
        状态详情,
        __taskKey: `${item['任务号'] || ''}-${i}`
      };
    },

    /** 点「规格型号」蓝色链接 → 展开 / 收起该行（代替原来的左侧箭头）
        数据和二级表格的加载仍然走 @expandChange → onExpandChange */
    onModelLinkClick({ row }) {
      const table = this.$refs.orderTable;
      if (table) table.toggleRowExpansion(row);
    },

    /** 展开 / 收起：只有「没拉过、也不在请求中」才请求（收起再展开沿用已加载的数据和页码） */
    onExpandChange(row) {
      if (!row.__taskLoaded && !row.__taskLoading) this.fetchTasks(row, 1);
    },

    async fetchTasks(row, pageNo) {
      if (!this.contractNo) return;
      row.__taskPage = pageNo;
      row.__taskLoading = true;

      const api = this.isFromProject ? getTasksListFromProject : getTasksListFromContract;

      // 基础参数按接口约定：contractNo + 父项型号 + 分页
      const params = {
        contractNo: this.contractNo,
        model: row.FModel || '',
        pageNo: String(pageNo),
        pageSize: String(row.__taskSize)
      };
      // 二级表格的列筛选：填了才传，没填保持和接口约定一致
      const f = row.__filters || {};
      if (f['任务号']) params.taskNo = f['任务号'];
      if (f['生产车间']) params.department = f['生产车间'];
      if (f['生产进度']) params.productionProgress = f['生产进度'];
      if (f['计划完工日期']) params.planFinishDate = f['计划完工日期'];
      if (f['数据状态码']) params.isIssue = f['数据状态码'];
      if (f['数量'] !== undefined && f['数量'] !== '') params.qty = String(f['数量']);

      try {
        const res = await api(params);
        const payload = res && res.data && res.data.data;
        const info = payload && typeof payload === 'object' ? payload.information : null;
        if (res && res.data && res.data.code === 200 && info) {
          row.__tasks = (info.list || []).map((item, i) => this.normalizeTask(item, i));
          row.__taskTotal = Number(info.total) || 0;
        } else {
          row.__tasks = [];
          row.__taskTotal = 0;
        }
        row.__taskLoaded = true;
      } catch (e) {
        row.__tasks = [];
        row.__taskTotal = 0;
        this.$message.error('任务单列表加载失败，请检查网络');
      } finally {
        row.__taskLoading = false;
      }
    },

    onTaskPageChange(row, p) {
      this.fetchTasks(row, p);
    },
    onTaskSizeChange(row, s) {
      row.__taskSize = s;
      this.fetchTasks(row, 1);
    },
    /** 二级列筛选变化：回到第一页重新查 */
    onTaskFilterChange(row, filterValues) {
      row.__filters = { ...(filterValues || {}) };
      this.fetchTasks(row, 1);
    },

    /** 任务单层级「生产」按钮：交给父组件走原来的生产跳转 */
    onTaskProductionClick(row) {
      this.$emit('production-click', row);
    },

    /* ================= 三·B、二级：BOM 列表（页签） ================= */
    /** 切换页签：切到 BOM 且没拉过才请求（懒加载，来回切不重复查） */
    onSubTabClick(row, tab) {
      // 优先取事件里的 tab.name，避免依赖 v-model 的更新时机
      const name = (tab && tab.name) || row.__subTab;
      if (name === 'bom' && !row.__bomLoaded && !row.__bomLoading) {
        this.fetchBoms(row, 1);
      }
    },

    /** BOM 行上的短代码：接口参数 fsn 取订单行的「短代码」 */
    pickFsn(row) {
      const v = row.fsn || row.FSN || row.短代码 || row.serialCode || '';
      return v === null || v === undefined ? '' : String(v).trim();
    },

    /** 拉 BOM 列表：参数 { fsn, pageNo, pageSize } */
    async fetchBoms(row, pageNo) {
      const fsn = this.pickFsn(row);
      row.__bomPage = pageNo;

      if (!fsn) {
        row.__bomData = [];
        row.__bomTotal = 0;
        row.__bomLoaded = true;
        this.$message.warning('该订单缺少短代码，无法查询 BOM');
        return;
      }

      row.__bomLoading = true;
      try {
        const res = await getBomsListFromContract({
          fsn,
          pageNo: String(pageNo),
          pageSize: String(row.__bomSize)
        });
        const payload = res && res.data && res.data.data;
        const list = payload && Array.isArray(payload.list) ? payload.list : [];

        if (res && res.data && res.data.code === 200 && payload && typeof payload === 'object') {
          // 后端返回了 bomColumns 里没定义的字段 → 记下来，自动补到最后一列（不丢数据）
          this.collectExtraBomFields(list);
          row.__bomData = list.map((item, i) => this.normalizeBom(item, i, fsn, pageNo));
          row.__bomTotal = Number(payload.total) || 0;
        } else {
          row.__bomData = [];
          row.__bomTotal = 0;
        }
        row.__bomLoaded = true;
      } catch (e) {
        row.__bomData = [];
        row.__bomTotal = 0;
        this.$message.error('BOM列表加载失败，请检查网络');
      } finally {
        row.__bomLoading = false;
      }
    },

    /** 收集 bomColumns 里没定义的字段（后端以后加字段时自动补列，不用改前端） */
    collectExtraBomFields(list) {
      const known = this.bomColumns.map(c => c.prop);
      (list || []).forEach(item => {
        if (!item || typeof item !== 'object') return;
        Object.keys(item).forEach(key => {
          if (String(key).indexOf('__') === 0) return;
          if (known.indexOf(key) === -1 && this.bomExtraColumns.indexOf(key) === -1) {
            this.bomExtraColumns.push(key);
          }
        });
      });
    },

    /** BOM 单元格格式化：number 去掉多余的 0（1.0000000000 → 1，1.5000 → 1.5），空值 → ''
     *  ⚠️ 只处理 number：字符串必须原样返回，否则短代码「017043」会被转成 17043 丢掉前导 0 */
    formatBomCell(val) {
      if (val === null || val === undefined) return '';
      if (typeof val === 'number' && !isNaN(val)) {
        return String(Math.round(val * 10000) / 10000);
      }
      return String(val);
    },

    normalizeBom(item, i, fsn, pageNo) {
      const out = { __bomKey: `${fsn}-${pageNo}-${i}` };
      Object.keys(item).forEach(key => {
        out[key] = this.formatBomCell(item[key]);
      });
      return out;
    },

    onBomPageChange(row, p) {
      this.fetchBoms(row, p);
    },

    onBomSizeChange(row, s) {
      row.__bomSize = s;
      this.fetchBoms(row, 1);
    },

    /* ================= 四、任务单编辑 / 异常详情 ================= */
    getCurrentUserCode() {
      const userCode = UserInfoUtils.getUserCode();
      if (!userCode) {
        this.$message.warning('未获取到用户信息，请重新登录');
        this.$router.push('/login');
        return null;
      }
      return userCode;
    },

    /** 任务单执行反馈保存（inline 编辑，接口与 workOrderProgress 一致） */
    async handleTextEditSave(data) {
      const userCode = this.getCurrentUserCode();
      if (!userCode) return;
      if (!data.row.任务号) {
        this.$message.warning('任务号不能为空');
        return;
      }
      try {
        const res = await UpdateDataList({
          Type: 'UpdateTaskContent',
          msg: JSON.stringify({
            taskNum: data.row.任务号,
            updateBy: userCode,
            context: data.value || ''
          })
        });
        if (res.data.code === 200) {
          this.$message.success('反馈内容保存成功');
          // 用本行对象直接同步（排序后下标不可靠）
          this.$set(data.row, '任务单执行反馈', data.value);
        } else {
          this.$message.error('保存失败：' + (res.data.message || '未知错误'));
        }
      } catch (err) {
        this.$message.error('网络错误，请检查连接');
      }
    },

    /** 派工日期保存（inline 编辑，接口与 workOrderProgress 一致） */
    async handleDateEditSave(data) {
      const userCode = this.getCurrentUserCode();
      if (!userCode) return;
      if (!data.row.任务号) {
        this.$message.warning('任务号不能为空');
        return;
      }
      if (!data.value) {
        this.$message.warning('请选择派单日期');
        return;
      }
      try {
        const res = await UpdateDataList({
          Type: 'UpdateTaskInfo',
          msg: JSON.stringify({
            dispatchDate: data.value,
            updateBy: userCode,
            taskNum: data.row.任务号
          })
        });
        if (res.data.code === 200) {
          this.$message.success('派工日期保存成功');
          this.$set(data.row, '派工日期', data.value);
        } else {
          this.$message.error('保存失败：' + (res.data.message || '未知错误'));
        }
      } catch (error) {
        this.$message.error('网络错误，请检查连接');
      }
    },

    /** 状态图标点击：仅异常行弹出异常详情（逻辑同 workOrderProgress） */
    async handleStatusIconClick({ row }) {
      if (row['数据状态码'] !== 0 && row['数据状态码'] !== '0') return;
      const taskNo = row['任务号'];
      if (!taskNo) {
        this.$message.warning('该行没有任务号');
        return;
      }
      this.currentExceptionTaskNo = taskNo;
      try {
        const res = await getAbnormalInfo({ taskNo, contractNo: '', projectNo: '' });
        if (res.data.code === 200 && res.data.data && res.data.data.exceptionInfo) {
          this.exceptionList = res.data.data.exceptionInfo.map((item) => ({
            项目编号: item['项目编号'] || '',
            合同编号: item['合同编号'] || '',
            任务号: item['任务号'] || '',
            相关节点人: item['相关节点人'] || '',
            异常类型: item['异常类型'] || '',
            事件详情: item['事件详情'] || '',
            发生时间: item['发生时间'] || '',
            是否需要处理: item['是否需要处理'] || '',
            处理人: item['处理人'] || '',
            处理时间: item['处理时间'] || '',
            解决时间: item['解决时间'] || ''
          }));
          this.exceptionDialogVisible = true;
        } else {
          this.$message.info('该任务单暂无异常详情');
        }
      } catch (error) {
        this.$message.error('查询异常详情失败');
      }
    },

    closeExceptionDialog() {
      this.exceptionDialogVisible = false;
      this.exceptionList = [];
      this.currentExceptionTaskNo = '';
    },

    /* ================= 五、导出（一级订单列表） ================= */
    handleExport() {
      const table = document.querySelector('#orderProgressTable');
      if (!table) {
        this.$message.warning('没有可导出的内容');
        return;
      }
      const wb = XLSX.utils.table_to_book(table, { raw: true });
      const wbout = XLSX.write(wb, { bookType: 'xlsx', bookSST: true, type: 'array' });
      try {
        FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), '订单列表.xlsx');
      } catch (e) {
        this.$message.error('导出失败，请重试');
      }
    }
  }
};
</script>

<style scoped>
.orderProgress {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 一级表格占满剩余高度，内容多时在表格内部滚动，不把卡片撑破 */
.op-table-wrap {
  flex: 1;
  min-height: 0;
}

/* 二级（展开区）：灰底 + 缩进，和一级区分开 */
.sub-wrap {
  padding: 8px 12px 10px;
  box-sizing: border-box;
  background: #f7f9fc;
}
.sub-title {
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #2564cf;
}
/* 任务单多的时候在展开区内部滚动，不把展开行撑得特别高 */
.sub-table {
  max-height: 340px;
  overflow: auto;
}

/* ✅ 展开区页签：压紧表头，别给展开行额外增高；滚动交给 .sub-table 自己 */
.sub-tabs ::v-deep .el-tabs__header {
  margin: 0 0 6px;
}
.sub-tabs ::v-deep .el-tabs__nav-wrap::after {
  height: 1px;
}
.sub-tabs ::v-deep .el-tabs__item {
  height: 30px;
  line-height: 30px;
  font-size: 12px;
}
.sub-tabs ::v-deep .el-tabs__content {
  overflow: visible;
}
.sub-paging {
  margin-top: 6px;
  display: flex;
  justify-content: flex-end;
}

/* ⚠️ contractinfo 里有一条「.task-section 下所有 el-table 都 flex:1 + height:100%」的规则，
   会把二级表格也拉成撑满高度。这里用更高优先级 + !important 抵消掉，让二级表格按自身内容排布。 */
.sub-table ::v-deep .el-table {
  flex: none !important;
  height: auto !important;
  display: block !important;
}

/* ⚠️ 自带的「展开 / 收起」箭头不再显示
   （那一列的 type="expand" 必须保留：Element 靠它渲染展开行的内容，
     真删掉的话 expand-row-keys 生效但展开行里是空的）
   这里只把它压到 1px 并把箭头图标隐藏，视觉上就跟没有这一列一样，
   实际由「规格型号」的蓝色链接来触发展开 / 收起。 */
.orderProgress ::v-deep #orderProgressTable .el-table__expand-column {
  width: 1px !important;
  padding: 0 !important;
}
.orderProgress ::v-deep #orderProgressTable .el-table__expand-icon {
  display: none !important;
}

/* ============================================================
   一级（订单）列表配色：表头 / 表体背景色与 afterSaleNoTable.vue 完全一致
   （同一套主题蓝 rgba(37, 100, 207, x)：表头 .12、表体 .06）
   ⚠️ 必须写「>」直接子元素链！
      二级表格是渲染在一级表格的展开行里的（#orderProgressTable > … > td.el-table__expanded-cell
      > … > 二级 el-table），用普通后代选择器会把二级表格的表头 / 表体一起刷成同一个底色。
      写成一级 el-table 的直接子元素，就只命中一级表格自身的 DOM 层了。
   ============================================================ */

/* 表头底色 */
.orderProgress ::v-deep #orderProgressTable > .el-table__header-wrapper > .el-table__header > thead > tr > th {
  background-color: rgba(37, 100, 207, 0.12) !important;
}
/* 表体底色 */
.orderProgress ::v-deep #orderProgressTable > .el-table__body-wrapper > .el-table__body > tbody > tr > td {
  background-color: rgba(37, 100, 207, 0.06) !important;
}
/* 滚动区：行数不足一屏时也别露白 */
.orderProgress ::v-deep #orderProgressTable > .el-table__body-wrapper {
  background-color: rgba(37, 100, 207, 0.06);
}
/* 选中行（afterSaleNoTable 同款，本页暂未开行高亮，留着备用） */
.orderProgress ::v-deep #orderProgressTable > .el-table__body-wrapper > .el-table__body > tbody > tr.selected-row > td {
  background-color: rgba(37, 100, 207, 0.2) !important;
  font-weight: 500;
}
/* 行悬停：比表体再深一点，字色保持 Element 默认不动（这里不再改任何 color） */
.orderProgress ::v-deep #orderProgressTable > .el-table__body-wrapper > .el-table__body > tbody > tr:hover > td {
  background-color: rgba(37, 100, 207, 0.12) !important;
}

/* 二级（任务单）表格：显式钉回 card-table 的默认配色，避免任何残留外溢
   （它不是 #orderProgressTable 的直接子元素，理论上不会命中，这里再兜一层保险） */
.orderProgress .sub-table ::v-deep .el-table__header thead th {
  background-color: rgba(37, 100, 207, 0.06) !important;
}
.orderProgress .sub-table ::v-deep .el-table__body tbody td {
  background-color: transparent !important;
}
.orderProgress .sub-table ::v-deep .el-table__body-wrapper {
  background-color: transparent;
}

/* ============================================================
   手机端（≤768px）与电脑端显示完全一致
   ------------------------------------------------------------
   card-table 在手机端会把字号压到 10px、表头行高压到 30px、内边距也缩小，
   这里逐条还原成电脑端的值（12px / 36px / padding: 8px 2px 8px 1px）。
   配合上面传的 :mobile-columns="全部列" 和 :responsive="false"（PC 版分页），
   手机和电脑看到的就是同一张表。
   ⚠️ 选择器都带 .orderProgress + 包裹层，是为了压过 card-table 里同名的手机端规则。
   ============================================================ */
@media screen and (max-width: 768px) {
  /* ---- 一级：订单列表 ---- */
  .orderProgress .op-table-wrap ::v-deep .el-table td,
  .orderProgress .op-table-wrap ::v-deep .el-table__body td .cell,
  .orderProgress .op-table-wrap ::v-deep .el-table__header-wrapper thead th,
  .orderProgress .op-table-wrap ::v-deep .el-table__header-wrapper .el-table__header th .cell {
    font-size: 12px !important;
  }
  .orderProgress .op-table-wrap ::v-deep .el-table__body td .cell {
    padding: 8px 2px 8px 1px !important;
    line-height: 1.2 !important;
  }
  .orderProgress .op-table-wrap ::v-deep .el-table__header-wrapper thead th {
    height: 36px !important;
  }
  .orderProgress .op-table-wrap ::v-deep .el-table__header-wrapper .el-table__header th .cell {
    padding: 4px 2px !important;
    line-height: 1.2 !important;
  }
  .orderProgress .op-table-wrap ::v-deep .el-table .cell {
    line-height: 1.2 !important;
    padding-left: 1px !important;
    padding-right: 2px !important;
  }

  /* ---- 二级：任务单列表（同样还原成电脑端的字号 / 行高 / 内边距） ---- */
  .orderProgress .sub-table ::v-deep .el-table td,
  .orderProgress .sub-table ::v-deep .el-table__body td .cell,
  .orderProgress .sub-table ::v-deep .el-table__header-wrapper thead th,
  .orderProgress .sub-table ::v-deep .el-table__header-wrapper .el-table__header th .cell {
    font-size: 12px !important;
  }
  .orderProgress .sub-table ::v-deep .el-table__body td .cell {
    padding: 8px 2px 8px 1px !important;
    line-height: 1.2 !important;
  }
  .orderProgress .sub-table ::v-deep .el-table__header-wrapper thead th {
    height: 36px !important;
  }
  .orderProgress .sub-table ::v-deep .el-table__header-wrapper .el-table__header th .cell {
    padding: 4px 2px !important;
    line-height: 1.2 !important;
  }
  .orderProgress .sub-table ::v-deep .el-table .cell {
    line-height: 1.2 !important;
    padding-left: 1px !important;
    padding-right: 2px !important;
  }
  /* 二级表格的配色在上面已显式钉回 card-table 默认值（不跟着一级走），这里不再重复 */

  /* ---- 卡片头部：标题字号 / 间距也和电脑端一致 ---- */
  .orderProgress ::v-deep .card-top {
    margin-bottom: 5px !important;
  }
  .orderProgress ::v-deep .card-top .card-title {
    font-size: 14px !important;
    line-height: 18px !important;
  }

  /* ---- 展开区尺寸不再单独压缩，和电脑端一样 ---- */
  .sub-wrap { padding: 8px 12px 10px; }
  .sub-title { font-size: 12px; }
  .sub-table { max-height: 340px; }
}
</style>
